import { useNavigate } from "react-router-dom";
import { Stethoscope, User, Activity, ArrowRight } from "lucide-react";

const DOCTOR_IMG =
  "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwYXRpZW50JTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc5MTg2MzcxOHww&ixlib=rb-4.1.0&q=85";
const PATIENT_IMG =
  "https://images.unsplash.com/photo-1758691461990-03b49d969495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBwYXRpZW50JTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc5MTg2MzcxOHww&ixlib=rb-4.1.0&q=85";

const RoleCard = ({ testid, title, desc, img, icon: Icon, onClick }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition-shadow duration-300 hover:shadow-xl"
  >
    <div className="h-44 w-full overflow-hidden">
      <img
        src={img}
        alt={title}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="flex flex-1 flex-col gap-3 p-7">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon strokeWidth={2} className="h-6 w-6" />
        </span>
        <h2 className="font-heading text-2xl font-semibold text-foreground">{title}</h2>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
      <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
        Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </button>
);

export default function RoleSelect() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background px-6 pb-20 pt-14">
      <div className="mx-auto max-w-5xl">
        <div className="mb-12 flex flex-col items-start gap-4">
          <span className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
            <Activity className="h-4 w-4" /> OncoDx Clinic
          </span>
          <h1 className="font-heading text-4xl font-semibold leading-tight text-foreground sm:text-5xl lg:text-6xl">
            Cancer diagnosis & care,
            <br />
            the way a clinician reasons.
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
            History → relevant questions → provisional differential → investigations → final
            diagnosis → evidence-based care plan & prognosis → follow-up monitoring. Choose how you'd
            like to begin.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <RoleCard
            testid="role-doctor-card"
            title="I'm a Clinician"
            desc="Drive the full diagnostic workflow with AI assistance you can review and override at every step."
            img={DOCTOR_IMG}
            icon={Stethoscope}
            onClick={() => navigate("/doctor/cases")}
          />
          <RoleCard
            testid="role-patient-card"
            title="I'm a Patient"
            desc="Share your history in simple terms and follow an easy, guided explanation of each step."
            img={PATIENT_IMG}
            icon={User}
            onClick={() => navigate("/patient/cases")}
          />
        </div>
      </div>
    </div>
  );
}
